import {QueryFunction, QueryKey, UseQueryOptions, useQuery} from '@tanstack/react-query';
import {Database} from '@tnc/shared';

import {sortOptionT} from '@/lib/types/dataTable.types';
import {limitT} from '@/lib/types/pagination.types';
import {
  BaseFilterT,
  ColumnSelectionT,
  CombinedColumnsT,
  FilterT,
  NotFilterT,
  OrFilterT,
  PaginationKeyT,
  SelectedColumnsKeysT,
} from '@/lib/types/query.types';
import {AllTablesT, FlattenT} from '@/lib/types/utility.types';

import {convertColumnsToString, getQueryKey} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useAuth} from '@/providers/components/authProvider';

/*******************************************************************
 * Types
 *******************************************************************/
// Type for the response of the useDataQuery hook
type UseDataQueryResponseT<
  T extends keyof AllTablesT,
  C extends ColumnSelectionT<T>[],
  R extends CombinedColumnsT<T, C> = CombinedColumnsT<T, C>,
> = {
  data: R[];
  total: number;
  currentPage: number;
  totalPages: number;
};

// Type for the props of the useDataQuery hook
export type UseDataQueryPropsT<
  T extends keyof Database['public']['Tables'],
  C extends ColumnSelectionT<T>[] = ['*'],
  TData = UseDataQueryResponseT<T, C>,
  TError extends Error = Error,
  TQueryFnData = UseDataQueryResponseT<T, C>,
  TQueryKey extends QueryKey = QueryKey,
> = FlattenT<
  Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'queryKey' | 'queryFn' | 'queryHash' | 'queryKeyHashFn'
  > & {
    table: T;
    columns?: C;
    enabled?: boolean;
    page?: number;
    limit?: limitT;
    sortOption?: sortOptionT<T>;
    searchTerm?: string;
    searchColumn?: SelectedColumnsKeysT<T, C>;
    all?: boolean;
    filters?: FilterT<T, C>[];
    protect?: boolean;
    authOverride?: boolean;
  }
>;

// Type for the props of the fetchData function
type FetchDataPropsT<T extends keyof AllTablesT, C extends ColumnSelectionT<T>[]> = {
  table: T;
  columns?: C;
  sortDirection?: 'asc' | 'desc';
  page?: number | null;
  limit?: limitT | null;
  searchTerm?: string;
  searchColumn?: SelectedColumnsKeysT<T, C>;
  sortColumn?: keyof Database['public']['Tables'][T]['Row'];
  all?: boolean;
  enabled?: boolean;
  filters?: FilterT<T, C>[];
};

/*********************************************************************
 * Helper Functions
 *********************************************************************/
// Function to validate the paginationKey
function isPaginationKey(value: string): value is PaginationKeyT {
  return value === 'all' || /^page:\d+ limit:\d+$/.test(value);
}

// Fetches data from the database
async function fetchData<
  T extends keyof AllTablesT,
  C extends ColumnSelectionT<T>[],
  R extends CombinedColumnsT<T, C> = CombinedColumnsT<T, C>,
>({
  table,
  columns = ['*'] as C,
  sortDirection = 'desc',
  page = null,
  limit = null,
  searchTerm = '',
  searchColumn = `${String(table).toLowerCase()}_name` as SelectedColumnsKeysT<T, C>,
  sortColumn = 'date_created' as keyof Database['public']['Tables'][T]['Row'],
  all = !page && !limit,
  filters = [],
}: FetchDataPropsT<T, C>) {
  let query = SupabaseInstance.supabase
    .from(table)
    .select<string, R>(convertColumnsToString(columns), {count: 'exact'})
    .throwOnError();

  // Apply search if searchTerm is provided
  if (searchTerm) {
    query = query.ilike(String(searchColumn), `%${searchTerm}%`);
  }

  // Apply filters
  filters.forEach(filter => {
    // Type guard to determine the type of filter
    function isBaseFilter(filter: FilterT<T, C>): filter is BaseFilterT<T, C> {
      return 'column' in filter && 'operator' in filter && 'value' in filter;
    }

    function isNotFilter(filter: FilterT<T, C>): filter is NotFilterT<T, C> {
      return 'not' in filter;
    }

    function isOrFilter(filter: FilterT<T, C>): filter is OrFilterT<T, C> {
      return 'or' in filter;
    }

    // Handle different filter types
    if (isNotFilter(filter) && filter.not) {
      // ---------------------------------------------------------
      // Handle NOT filter
      // ------------------------------------------------
      const {column, operator, value} = filter.not;
      if (!column || !operator) throw new Error('Column is required for NOT filter');
      if (!Array.isArray(value)) throw new Error('Value must be an array for NOT IN operator');
      const formattedValues = `(${value.join(',')})`;
      query = query.not(String(column), operator, formattedValues);
    } else if (isOrFilter(filter) && filter.or) {
      // ---------------------------------------------------------
      // Handle OR filter
      // ---------------------------------------------------------
      const orConditions = filter.or
        .map(orFilter => {
          const {column: orColumn, operator: orOperator, value: orValue} = orFilter;
          if (!orColumn || !orOperator) throw new Error('Column, operator, and value are required for OR filter');
          return `${String(orColumn)} ${orOperator} ${orValue}`;
        })
        .join(' OR ');
      query = query.or(orConditions);
    } else if (isBaseFilter(filter)) {
      // ---------------------------------------------------------
      // Handle base filters
      // ---------------------------------------------------------
      const {column, operator, value} = filter;
      if (!column || !operator) throw new Error('Column, operator, and value are required for filter');

      // We need to use type assertion since FilterT is generic and Supabase methods have specific expectations
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const filterValue = value as any;

      switch (operator) {
        case 'eq':
          if (value === null || value === undefined)
            throw new Error('Value cannot be null or undefined for EQ operator, use IS operator instead');
          query = query.eq(column, filterValue);
          break;
        case 'neq':
          query = query.neq(column, filterValue);
          break;
        case 'gt':
          query = query.gt(column, filterValue);
          break;
        case 'gte':
          query = query.gte(column, filterValue);
          break;
        case 'lt':
          query = query.lt(column, filterValue);
          break;
        case 'lte':
          query = query.lte(column, filterValue);
          break;
        case 'like':
          query = query.like(column, String(filterValue));
          break;
        case 'ilike':
          query = query.ilike(column, String(filterValue));
          break;
        case 'in':
          if (!Array.isArray(filterValue)) throw new Error('Value must be an array for IN operator');
          query = query.in(column, filterValue);
          break;
        case 'contains':
          query = query.contains(column, filterValue);
          break;
        case 'containedBy':
          query = query.containedBy(column, filterValue);
          break;
        case 'rangeLt':
          query = query.rangeLt(column, filterValue);
          break;
        case 'rangeGt':
          query = query.rangeGt(column, filterValue);
          break;
        case 'rangeGte':
          query = query.rangeGte(column, filterValue);
          break;
        case 'rangeLte':
          query = query.rangeLte(column, filterValue);
          break;
        case 'rangeAdjacent':
          query = query.rangeAdjacent(column, filterValue);
          break;
        case 'overlaps':
          query = query.overlaps(column, filterValue);
          break;
        case 'is':
          if (filterValue === null || typeof filterValue === 'boolean') {
            query = query.is(column, filterValue);
          } else {
            throw new Error('Value must be null or boolean for IS operator, use EQ operator for other types of values');
          }
          break;
        default:
          throw new Error(`Unsupported operator: ${operator}`);
      }
    } else {
      throw new Error('Invalid filter');
    }
  });

  // Apply sorting
  query = query.order(String(sortColumn), {ascending: sortDirection === 'asc'});

  // Apply pagination if not fetching all
  if (!all) {
    if (page !== null && limit !== null) {
      query = query.range((page - 1) * limit, page * limit - 1);
    }
  }

  const {data, error, count} = await query;

  if (error) {
    throw new Error(`Error fetching ${String(table)}: ${error}`);
  }

  const totalPages = limit ? Math.ceil((count ?? 0) / limit) : 1;

  const response: UseDataQueryResponseT<T, C> = {
    data: data,
    total: count ?? 0,
    totalPages: totalPages ?? 1,
    currentPage: page ?? 1,
  };

  return response;
}

/*********************************************************************
 * useDataQuery Hook
 * Wrapper around useQuery to fetch data from the database
 *********************************************************************/
export const useDataQuery = <T extends keyof Database['public']['Tables'], C extends ColumnSelectionT<T>[]>({
  table,
  columns = ['*'] as C,
  enabled: _enabled = true,
  page = undefined,
  limit = undefined,
  all = (!page && !limit) ?? true,
  sortOption = {column: 'date_created', direction: 'desc', label: 'Date Created'} as sortOptionT<T>,
  searchColumn = `${String(table).toLowerCase()}_name` as SelectedColumnsKeysT<T, C>,
  searchTerm = '',
  filters = [],
  protect,
  authOverride = false,
  ...rest
}: UseDataQueryPropsT<T, C>) => {
  const {isAdministrator, isAuthenticated} = useAuth();
  const enabled = (protect ? isAdministrator && _enabled : _enabled) && (authOverride || isAuthenticated);

  const paginationKey = page !== undefined && limit !== undefined ? `page:${page} limit:${limit}` : 'all';
  if (!isPaginationKey(paginationKey)) throw new Error('Invalid pagination key');

  const queryKey = all
    ? getQueryKey.all(table, {columns, enabled, filters, sortOption, searchTerm, searchColumn})
    : getQueryKey.list(table, {
        columns,
        enabled,
        filters,
        sortOption,
        page: page ?? 1,
        limit: limit ?? 5,
        searchTerm,
        searchColumn,
      });

  const queryFn: QueryFunction<UseDataQueryResponseT<T, C>, QueryKey> = async () => {
    return fetchData<T, C>({
      table,
      sortColumn: sortOption.column,
      sortDirection: sortOption.direction,
      page,
      limit,
      columns,
      enabled,
      searchTerm,
      searchColumn,
      all,
      filters,
    });
  };

  // Options for the query
  const queryOptions: UseQueryOptions<UseDataQueryResponseT<T, C>, Error, UseDataQueryResponseT<T, C>, QueryKey> = {
    enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    retryOnMount: false,
    ...rest,
    queryKey,
    queryFn,
  };

  const queryResult = useQuery(queryOptions);

  return {
    ...queryResult,
    pagination: {
      currentPage: queryResult.data?.currentPage ?? 1,
      totalPages: queryResult.data?.totalPages ?? 1,
      totalItems: queryResult.data?.total ?? 0,
    },
  };
};
